import React, { useState, useEffect, useRef, useId } from 'react';
import { GetRequest, PostRequest } from 'components/common';
import UserUnitPriceCreateForm from 'components/quote_builder/UserUnitPriceCreateForm.jsx';
import AttributePropertyTags, { findFieldDefinition } from 'components/quote_builder/attributePropertyTags.jsx';

const MM_PER_INCH = 25.4;

// Convert a length between the machine's stored unit and the user's display unit (mm/in only).
const convertLength = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) { return value; }
  if (fromUnit === 'mm' && toUnit === 'in') { return value / MM_PER_INCH; }
  if (fromUnit === 'in' && toUnit === 'mm') { return value * MM_PER_INCH; }
  return value;
};

// At most 2 decimals, trailing zeros dropped: 520 -> "520", 20.4724 -> "20.47".
const formatLength = (value) => Number(value.toFixed(2)).toString();

// The "copy from the library" view shown when a user adds a machine (settings page and /mcc). It loads every
// curated machine of this key owned by the library account once, filters them in the browser as the user types
// (instant, no per-keystroke request), and copies the chosen one into the user's own account -- then that
// machine's price form REPLACES the search in place (a machine with no price cannot quote a line; onPriced fires
// on save). "Enter manually" calls onManualEntry, which the embedder handles (its own blank machine form).
//
// allowedTypes, when given, restricts the library to machines whose library_filter_property (e.g. a printing
// machine's printing_type) is one of the listed values. /mcc's add-machine picker passes the section's usable
// printing types so a carton picker never offers a Flexo or Serigraf press the carton line could not use. Absent
// (the settings page) shows every type.
// libraryAttributeIds, when given (an array, possibly empty), further keeps only the library machines with those ids
// -- the /mcc guided setup passes the ones that can make a manufacturing line for the section's part_type
// (Frank::SectionSetupRequirements), which allowedTypes alone cannot tell (a die-cutter's type also counts). Absent
// everywhere else, which leaves the list as allowedTypes made it.
// onPriced fires after the copied machine's price is saved (the search then returns for the next machine) -- an
// embedder that must re-solve on a priced machine passes it. hideDone hides the "Done" button where the flow's
// exit is not "leave the picker" (the /mcc inline add-machine step, where you pick a machine and price it).
//
// entryOnly stops after the copy: the machine is created and onCopy fires, but its price form does NOT replace
// the search -- pricing is a separate step elsewhere (the wizard's own machine-price stage). Used where adding
// the machine and pricing it are two distinct, separately-counted steps.
const LibraryAttributePicker = ({
  userUnitPriceKey, tabConfig, onCopy, onManualEntry, onClose, onPriced, hideDone, allowedTypes, libraryAttributeIds, entryOnly,
}) => {
  const [libraryAttributes, setLibraryAttributes] = useState([]);
  const [subFormAttributes, setSubFormAttributes] = useState(undefined);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState(null);
  const [error, setError] = useState('');
  const [activeType, setActiveType] = useState(null);
  const [activeSelects, setActiveSelects] = useState({});
  const [activeDimension, setActiveDimension] = useState('');
  // Ids of the library machines already added this session; hidden from the list so the user can add
  // several in a row without the popup closing.
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Copying a machine from the library is not finished until it can be QUOTED with -- a machine with no
  // UserUnitPrice cannot price a manufacturing line. So the moment a machine is copied, its price form REPLACES
  // the search here (`pricingMachine` = the copied machine awaiting a price, or null), the owner adds the price,
  // and the search returns for the next one. Same in every embedder (settings page, /mcc manage popup and the
  // /mcc inline add-machine step) -- what differs is only what each does on copy (onCopy) and on save (onPriced).
  const [pricingMachine, setPricingMachine] = useState(null);
  const searchInputRef = useRef();
  // A unique id per mounted picker: a QuoteBuilderForm can render two library-enabled machine fields inline
  // (e.g. a printing and a die-cutting machine, both set to "create new"), so a fixed id would duplicate.
  const searchInputId = useId();

  const sectionKey = tabConfig.key;

  // Copy is per-attribute: a tab may name its own title and search-placeholder i18n keys (so die-cutting
  // reads "Add a die-cutting machine", not "printing"); each falls back to the shared default. The
  // subtitle stays shared -- "Find your machine and adjust it however you like" fits every machine type.
  const titleKey = tabConfig.library_picker_title || 'quote_builder.library_picker_title';
  const searchPlaceholderKey = tabConfig.library_picker_search_placeholder || 'quote_builder.library_picker_search_placeholder';

  // Search matches the machine name AND its shown attributes, so typing "offset" (or "dijital", a sheet
  // size, a colour count) finds machines of that kind even though the word is not in the name. Both the
  // raw stored value and the localized select label are included, so it works in either language.
  const searchableText = (attribute) => {
    const parts = [attribute.name, attribute.name_en, attribute.name_tr];
    const properties = attribute.properties || {};
    const pushValue = (field, value) => {
      parts.push(String(value));
      const pair = (field?.form_type === 'select' || field?.form_type === 'checkboxes')
        ? field.values?.find((option) => String(option.value) === String(value))
        : undefined;
      if (pair) { parts.push(t(pair.label)); }
    };
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (value === null || value === undefined) { return; }
      const field = findFieldDefinition(key, subFormAttributes);
      // A checkboxes property (e.g. die-cutting "cuttable materials") stores an array; index each entry.
      if (Array.isArray(value)) {
        value.forEach((entry) => { if (typeof entry !== 'object') { pushValue(field, entry); } });
      } else if (typeof value !== 'object') {
        pushValue(field, value);
      }
    });
    return parts.filter(Boolean).join(' ').toLocaleLowerCase();
  };

  const matchesQuery = (attribute) => {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === '') { return true; }
    return searchableText(attribute).includes(needle);
  };

  // Compares a machine's stored value for `property` against `value`, handling both scalar select fields
  // and multi-value checkboxes fields whose stored value is an array (e.g. die-cutting "cuttable
  // materials"): an array matches when it contains the value. Keeps every filter attribute-type-agnostic.
  const propertyMatchesValue = (attribute, property, value) => {
    const stored = attribute.properties?.[property];
    if (Array.isArray(stored)) { return stored.some((entry) => String(entry) === String(value)); }
    return String(stored) === String(value);
  };

  // Machines the current search can still reach: not already added this session and matching the query.
  // The type pills and both dropdowns grey out a facet no reachable machine has, so every filter reflects
  // what the search can actually reach -- searching "canon" greys out Flexo, and any colour count or size
  // that only non-Canon machines have.
  const reachableBySearch = (attribute) => !addedIds.has(attribute.id) && matchesQuery(attribute);

  // Optional quick-filter pills for one property (e.g. "Baskı türü" / printing_type). The property is
  // named in the tab config; its options + labels are reused from the create sub-form field so the
  // filter always mirrors the form. Only the option values actually present in the library are shown.
  const filterProperty = tabConfig.library_filter_property;
  const filterField = filterProperty ? findFieldDefinition(filterProperty, subFormAttributes) : null;
  const typeOptions = (filterField?.values || []).filter((option) =>
    libraryAttributes.some((attribute) => propertyMatchesValue(attribute, filterProperty, option.value))
  );

  const matchesType = (attribute) => {
    if (activeType === null || filterProperty === undefined) { return true; }
    return propertyMatchesValue(attribute, filterProperty, activeType);
  };

  // Additional select fields rendered as dropdowns (e.g. "Baskı ünite sayısı" / printing_unit_count, or
  // die-cutting "Machine type"). Each lists the values present among the type-filtered machines, so the
  // choices track the type; an option the current search cannot reach is kept but greyed out (disabled),
  // so a live selection never vanishes and the picture stays stable as you type.
  const selectFilters = (tabConfig.library_select_filters || []).map((filterConfig) => {
    const field = findFieldDefinition(filterConfig.property, subFormAttributes);
    const machinesOfType = libraryAttributes.filter(matchesType);
    const hasValue = (pool, value) =>
      pool.some((attribute) => propertyMatchesValue(attribute, filterConfig.property, value));

    // A field with static choices (select/checkboxes) supplies value -> i18n label. A field with none
    // (e.g. an attribute reference the payload already resolved to its name, like die_cutting_machine_type)
    // derives its choices from the distinct values present, each labelled by the value itself.
    const baseOptions = field?.values?.length
      ? field.values.map((option) => ({ value: option.value, displayLabel: t(option.label) }))
      : Array.from(
        machinesOfType.reduce((byValue, attribute) => {
          const value = attribute.properties?.[filterConfig.property];
          if (value === undefined || value === null || value === '' || Array.isArray(value)) { return byValue; }
          if (!byValue.has(String(value))) { byValue.set(String(value), { value, displayLabel: String(value) }); }
          return byValue;
        }, new Map()).values()
      ).sort((first, second) => first.displayLabel.localeCompare(second.displayLabel));

    const options = baseOptions
      .filter((option) => hasValue(machinesOfType, option.value))
      .map((option) => ({
        ...option,
        disabled: !hasValue(machinesOfType.filter(reachableBySearch), option.value),
      }));
    // The whole dropdown is disabled when it offers nothing useful: no option the search can reach and
    // nothing currently selected to clear. A live selection keeps it enabled so a stale filter can be cleared.
    const selectDisabled = options.every((option) => option.disabled) && !activeSelects[filterConfig.property];
    return { property: filterConfig.property, allLabel: filterConfig.all_label, options, selectDisabled };
  });

  const matchesSelects = (attribute) =>
    selectFilters.every((filter) => {
      const active = activeSelects[filter.property];
      return !active || propertyMatchesValue(attribute, filter.property, active);
    });

  // Optional size dropdown, generated dynamically from the data: the distinct maximum sheet sizes present
  // in the library, SCOPED to the currently selected type so switching "Baskı türü" (e.g. Flexo -> Offset)
  // refreshes the list to that type's sizes. A machine's max sheet is a rectangle, so both edges are kept;
  // options are smallest first, formatted "{y} × {x}" (property_y then property_x, matching the machine
  // dropdown labels) in the user's display unit (mm/in). Adding a machine with a new size adds an option
  // automatically -- there is nothing hardcoded to keep in sync.
  const displayUnit = window.user_unit === 'in' ? 'in' : 'mm';
  const dimensionFilter = tabConfig.library_dimension_filter;
  const dimensionInfo = (attribute) => {
    if (dimensionFilter === undefined) { return undefined; }
    const rawX = parseFloat(attribute.properties?.[dimensionFilter.property_x]);
    const rawY = parseFloat(attribute.properties?.[dimensionFilter.property_y]);
    if (Number.isNaN(rawX) || Number.isNaN(rawY)) { return undefined; }
    const storedUnit = attribute.properties?.[dimensionFilter.unit_property] || 'mm';
    const shown = (value) => `${formatLength(convertLength(value, storedUnit, displayUnit))}${displayUnit}`;
    // The match key stays in the stored value so filtering is unaffected by the display unit.
    return { x: rawX, y: rawY, key: `${rawX}x${rawY}`, label: `${shown(rawY)} × ${shown(rawX)}` };
  };
  // A size is greyed out (reachable: false) when the current search reaches no machine of that size within
  // the selected type + colour count; reachability is OR-ed across every machine sharing the size.
  const dimensionOptions = Array.from(
    libraryAttributes.filter(matchesType).filter(matchesSelects).reduce((byKey, attribute) => {
      const info = dimensionInfo(attribute);
      if (!info) { return byKey; }
      const reachable = Boolean(byKey.get(info.key)?.reachable) || reachableBySearch(attribute);
      byKey.set(info.key, { ...info, reachable });
      return byKey;
    }, new Map()).values()
  ).sort((first, second) => first.x - second.x || first.y - second.y);

  // The size dropdown is disabled when it offers nothing useful: no size the search can reach and no size
  // currently selected to clear.
  const sizeSelectDisabled = dimensionOptions.every((option) => !option.reachable) && activeDimension === '';

  // Funnel: the picker opened. Paired with the "enter manually" event below and the server-side
  // machine_copied_from_library event, this measures whether users who open the library actually
  // copy a machine from it -- i.e. whether the library is useful.
  useEffect(() => {
    if (typeof trackEvent === 'function') {
      trackEvent('library_picker_opened', { key: sectionKey });
    }
  }, []);

  useEffect(() => {
    // Field definitions (from the create sub-form) let the tags translate select codes like
    // printing_type into their labels, matching the settings list exactly.
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${userUnitPriceKey}/${sectionKey}`,
      callback: (data) => { setSubFormAttributes(data?.create_sub_form?.attributes); },
    });

    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/library/${sectionKey}`,
      callback: (data) => {
        const all = Array.isArray(data) ? data : [];
        // Drop machines of a type this embedder does not allow up front, so the count, the type pills and the
        // list all reflect only the usable types. Guarded on a non-empty list so "no restriction" never hides
        // everything.
        const usable = (allowedTypes && allowedTypes.length && filterProperty)
          ? all.filter((attribute) => allowedTypes.some((type) => propertyMatchesValue(attribute, filterProperty, type)))
          : all;
        const listed = Array.isArray(libraryAttributeIds)
          ? usable.filter((attribute) => libraryAttributeIds.includes(attribute.id))
          : usable;
        setLibraryAttributes(listed);
        setLoading(false);
      },
      onError: () => { setLoading(false); },
    });
  }, []);

  useEffect(() => {
    if (searchInputRef.current) { searchInputRef.current.focus(); }
  }, [loading]);

  // Esc closes the popup, matching standard modal behaviour.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && onClose) { onClose(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const matchesDimension = (attribute) => {
    if (activeDimension === '' || dimensionFilter === undefined) { return true; }
    return dimensionInfo(attribute)?.key === activeDimension;
  };

  // Whether any reachable (see reachableBySearch) machine has the given printing type. Used to grey out a
  // type pill the search rules out (e.g. searching "canon" disables "Flexo" when no Canon machine is a
  // flexo press), so the pills reflect what the current search can actually reach.
  const typeReachableByQuery = (typeValue) =>
    libraryAttributes.some((attribute) =>
      reachableBySearch(attribute)
      && propertyMatchesValue(attribute, filterProperty, typeValue)
    );

  // The "All types" pill is only meaningful while the search still reaches a machine of some type.
  const anyReachableByQuery = libraryAttributes.some(reachableBySearch);

  const filteredAttributes = libraryAttributes.filter((attribute) =>
    reachableBySearch(attribute)
    && matchesType(attribute) && matchesSelects(attribute) && matchesDimension(attribute)
  );

  const copy = (attribute) => {
    if (copyingId !== null) { return; }
    setError('');
    setCopyingId(attribute.id);
    const failed = () => {
      setCopyingId(null);
      setError(t('quote_builder.library_picker_copy_error'));
    };
    // PostRequest resolves its callback only on a 2xx and rejects otherwise, so success is the callback
    // and every failure (HTTP error or network) lands in .catch.
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/copy_from_library/${sectionKey}/${attribute.id}`,
      callback: (data) => {
        if (data && data.is === 'ok') {
          // Keep the popup open and hide the added row so more can be added; the parent refreshes counts
          // and, on close, the listing.
          setAddedIds((previous) => new Set(previous).add(attribute.id));
          setCopyingId(null);
          onCopy(data.new_attribute_id);
          // entryOnly: adding the machine IS the whole step here -- pricing it is a separate step elsewhere -- so
          // stop after the copy and let onCopy drive what happens next.
          if (entryOnly) { return; }
          // The machine is copied but not yet priceable -- lead the owner to add a price for it before moving on:
          // its price form REPLACES the search here (the early return below), and saving it calls onPriced. The
          // copy itself must stay side-effect-light -- onCopy decides whether it announces a change -- so on /mcc
          // the copy does NOT re-solve the section (which would remount and tear this down); the price save does.
          setPricingMachine({ id: data.new_attribute_id, name: attribute.name });
        } else {
          failed();
        }
      },
    }).catch(failed);
  };

  // "Enter manually" -- record that the user opened the library but chose the blank form instead
  // (a distinct signal from copying), before falling through to it. Closing via Done/x/Esc is not
  // tracked, so treat opened minus copied minus manual-entry as silent abandonment.
  const chooseManualEntry = () => {
    if (typeof trackEvent === 'function') {
      trackEvent('library_picker_manual_entry_chosen', { key: sectionKey });
    }
    onManualEntry();
  };

  const resultRow = (attribute) => (
    <div className="library-picker__row" key={attribute.id} onClick={() => copy(attribute)}>
      <div className="library-picker__row-info">
        <div className="library-picker__row-name">{attribute.name}</div>
        <AttributePropertyTags
          properties={attribute.properties}
          listingProperties={tabConfig.listing_properties}
          subFormAttributes={subFormAttributes}
        />
      </div>
      <button
        type="button"
        className="btn btn-default simple-btn-outline library-picker__select"
        onClick={(event) => { event.stopPropagation(); copy(attribute); }}
        disabled={copyingId !== null}
      >
        <i className="zmdi zmdi-open-in-browser" aria-hidden="true"></i>{' '}
        {copyingId === attribute.id ? t('quote_builder.library_picker_copying') : t('quote_builder.select_and_customise')}
      </button>
    </div>
  );

  // The price step: once a machine is copied, its price form REPLACES the search here, in place (no popup).
  // Saving returns to the search for the next machine AND tells the embedder (onPriced) -- which is where /mcc
  // re-solves the section, deferring the re-solve to the save so the copy alone never remounts this away.
  // Cancelling just returns to the search (the machine stays copied).
  if (pricingMachine) {
    return (
      // --pricing drops the picker's 78vh height cap: that cap is for the scrollable machine LIST, but here the
      // list is REPLACED by the full price form (no inner scroll of its own), so capping it hid the Save below the
      // fold. Uncapped, the form flows at its natural height and the page (or the modal) scrolls to the Save,
      // exactly as a page that loads straight into the price form already does.
      <div className="library-picker library-picker--pricing">
        <UserUnitPriceCreateForm
          priceKey={userUnitPriceKey}
          contextKey={sectionKey}
          attributeId={pricingMachine.id}
          afterSave={() => { setPricingMachine(null); if (onPriced) { onPriced(); } }}
          afterCancel={() => setPricingMachine(null)}
        />
      </div>
    );
  }

  return (
    <div className="library-picker">
      <div className="library-picker__header">
        <h3 className="library-picker__title">{t(titleKey)}</h3>
        <p className="library-picker__subtitle">{t('quote_builder.library_picker_subtitle')}</p>
      </div>

      <div className="library-picker__search-block">
        <label className="library-picker__search-label" htmlFor={searchInputId}>
          {t('quote_builder.library_picker_search_label')}
        </label>
        <div className="library-picker__search">
          <i className="fa fa-search library-picker__search-icon" aria-hidden="true"></i>
          <input
            id={searchInputId}
            ref={searchInputRef}
            type="text"
            className="library-picker__search-input"
            placeholder={t(searchPlaceholderKey)}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                if (onClose) { onClose(); }
              } else if (event.key === 'Enter' && copyingId === null && filteredAttributes.length > 0) {
                copy(filteredAttributes[0]);
              }
            }}
          />
          {query !== '' && (
            <button type="button" className="library-picker__search-clear" onClick={() => setQuery('')} aria-label="clear">
              &times;
            </button>
          )}
        </div>
      </div>

      {(typeOptions.length > 1 || selectFilters.some((filter) => filter.options.length > 1) || dimensionOptions.length > 1) && (
        <div className="library-picker__filters">
          <div className="library-picker__filters-label">{t('quote_builder.library_picker_filters_label')}</div>
          {typeOptions.length > 1 && (
            <div className="library-picker__filter-pills">
              <button
                type="button"
                disabled={!anyReachableByQuery}
                className={`library-picker__filter ${activeType === null ? 'is-active' : ''}`}
                onClick={() => { setActiveType(null); setActiveSelects({}); setActiveDimension(''); }}
              >
                {t('quote_builder.library_picker_all_types')}
              </button>
              {typeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={!typeReachableByQuery(option.value)}
                  className={`library-picker__filter ${String(activeType) === String(option.value) ? 'is-active' : ''}`}
                  onClick={() => { setActiveType(activeType === option.value ? null : option.value); setActiveSelects({}); setActiveDimension(''); }}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>
          )}

          {(selectFilters.some((filter) => filter.options.length > 1) || dimensionOptions.length > 1) && (
            <div className="library-picker__filter-dropdowns">
              {selectFilters.map((filter) => filter.options.length > 1 && (
                <select
                  key={filter.property}
                  data-filter={filter.property}
                  className="library-picker__dimension-select"
                  disabled={filter.selectDisabled}
                  value={activeSelects[filter.property] || ''}
                  onChange={(event) => { setActiveSelects({ ...activeSelects, [filter.property]: event.target.value }); setActiveDimension(''); }}
                >
                  <option value="">{t(filter.allLabel)}</option>
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled}>{option.displayLabel}</option>
                  ))}
                </select>
              ))}

              {dimensionOptions.length > 1 && (
                <select
                  data-filter="dimension"
                  className="library-picker__dimension-select"
                  disabled={sizeSelectDisabled}
                  value={activeDimension}
                  onChange={(event) => setActiveDimension(event.target.value)}
                >
                  <option value="">{t('quote_builder.library_all_sizes')}</option>
                  {dimensionOptions.map((option) => (
                    <option key={option.key} value={option.key} disabled={!option.reachable}>{option.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {error !== '' && <div className="library-picker__error">{error}</div>}

      {!loading && filteredAttributes.length > 0 && (
        <div className="library-picker__count">
          {t(filteredAttributes.length === 1 ? 'quote_builder.library_picker_count_one' : 'quote_builder.library_picker_count')
            .replace('%{count}', filteredAttributes.length)}
        </div>
      )}

      <div className="library-picker__results">
        {loading && <div className="library-picker__status">{t('quote_builder.please_wait')}</div>}

        {!loading && libraryAttributes.length === 0 && (
          <div className="library-picker__status">{t('quote_builder.library_picker_empty')}</div>
        )}

        {!loading && libraryAttributes.length > 0 && filteredAttributes.length === 0 && (
          <div className="library-picker__status">
            {query.trim() !== ''
              ? t('quote_builder.library_picker_no_matches').replace('%{query}', query)
              : t('quote_builder.library_picker_no_results')}
          </div>
        )}

        {!loading && filteredAttributes.map(resultRow)}
      </div>

      <div className="library-picker__footer">
        <div className="library-picker__footer-manual">
          <span className="library-picker__footer-prompt">{t('quote_builder.library_picker_manual_prompt')}</span>
          <button type="button" className="dct-button dct-button--secondary" onClick={chooseManualEntry}>
            {t('quote_builder.library_picker_manual_cta')}
          </button>
        </div>
        <div className="library-picker__footer-actions">
          {addedIds.size > 0 && (
            <span className="library-picker__added-count">
              {t('quote_builder.library_picker_added_count').replace('%{count}', addedIds.size)}
            </span>
          )}
          {/* "Done" closes the picker (settings page, /mcc manage popup). The /mcc inline add-machine step passes
              hideDone: there the step is to pick a machine and price it, not to leave, so it shows no Done. */}
          {!hideDone && (
            <button type="button" className="dct-button dct-button--primary" onClick={onClose}>
              {t('quote_builder.library_picker_done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryAttributePicker;
